import { DateTime } from 'luxon';

export type TrialLicense = {
  cycleStartAt: string;
  expiresAt: string | null;
  holderType: 'USER' | 'TENANT';
  plan: { type: string; key: string };
};

export type TrialDayCounts = {
  trialDaysLeft: number;
  trialDaysTotal: number;
};

function toDateTime(value: string | Date): DateTime {
  return typeof value === 'string'
    ? DateTime.fromISO(value)
    : DateTime.fromJSDate(value);
}

function hasPaidLicense(licenses: { plan: { type: string } }[]): boolean {
  return licenses.some(
    (license) =>
      license.plan.type === 'PAID' || license.plan.type === 'FRIENDS',
  );
}

/** Prefer a running school trial; a lapsed trial must not hide a running one. */
function findTrialLicense<T extends TrialLicense>(
  licenses: T[],
): T | undefined {
  const trials = licenses.filter((license) => license.plan.type === 'TRIAL');
  const runningTrials = trials.filter(
    (license) => getTrialDayCounts(license) !== null,
  );
  return (
    runningTrials.find((license) => license.holderType === 'TENANT') ??
    runningTrials.at(0) ??
    trials.find((license) => license.holderType === 'TENANT') ??
    trials.at(0)
  );
}

function getTrialDayCounts(
  license: TrialLicense | null | undefined,
): TrialDayCounts | null {
  if (
    license?.plan.type !== 'TRIAL' ||
    !license.expiresAt ||
    !license.cycleStartAt
  ) {
    return null;
  }

  const cycleStartAt = toDateTime(license.cycleStartAt);
  const expiresAt = toDateTime(license.expiresAt);

  if (!cycleStartAt.isValid || !expiresAt.isValid) {
    return null;
  }

  const now = DateTime.now();
  if (now >= expiresAt) {
    return null;
  }

  const trialDaysTotal = Math.ceil(expiresAt.diff(cycleStartAt, 'days').days);
  const trialDaysLeft = Math.max(
    0,
    Math.ceil(expiresAt.diff(now, 'days').days),
  );

  if (trialDaysTotal <= 0) {
    return null;
  }

  return { trialDaysLeft, trialDaysTotal };
}

function isTrialLicenseExpired(license: TrialLicense): boolean {
  if (license.plan.type !== 'TRIAL' || !license.expiresAt) {
    return false;
  }

  const expiresAt = toDateTime(license.expiresAt);
  if (!expiresAt.isValid) {
    return false;
  }

  return DateTime.now() >= expiresAt;
}

/** True when the selected school or personal trial has ended. */
export function isPersonalTrialExpired(licenses: TrialLicense[]): boolean {
  const trialLicense = findTrialLicense(licenses);
  if (!trialLicense) {
    return false;
  }

  return isTrialLicenseExpired(trialLicense);
}

export function getPersonalTrialDayCounts(
  licenses: TrialLicense[],
): TrialDayCounts | null {
  if (hasPaidLicense(licenses)) {
    return null;
  }

  return getTrialDayCounts(findTrialLicense(licenses));
}
